import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const PrivacyPolicyPage = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-ink-50" data-testid="privacy-page">
      <div className="bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-950 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to XEA
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-coral-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-ink-950">Privacy Policy</h1>
            <p className="text-ink-500">Last updated: {currentDate}</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <div className="space-y-8 text-ink-700 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">1. Who We Are</h2>
              <p>
                XEA (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the AI creative
                platform at <a href="https://xe-a.com" className="text-coral-500 underline">https://xe-a.com</a>{" "}
                and the XEA Progressive Web App (PWA) / native app distributions
                packaged via Capacitor. We are the controller of the personal
                data we process about you. Contact:{" "}
                <a href="mailto:privacy@xe-a.com" className="text-coral-500 underline">
                  privacy@xe-a.com
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">2. Information We Collect</h2>
              <p className="font-semibold text-ink-950">a. Account data</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name and email address</li>
                <li>Hashed password (bcrypt, never stored in plain text)</li>
                <li>Subscription tier (Free, Starter, Premium, Pro, Platinum) and token balance</li>
                <li>Earnings balance, tip-transaction history, and token-activity ledger</li>
                <li>Active studio mode (Marketing, Designing, CGI, Movie) and per-mode onboarding state</li>
                <li>Referral code and referral graph (who invited whom)</li>
                <li>Admin flags (for staff accounts only)</li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">b. Content data</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Product URLs, captions, prompts, edit prompts, negative prompts</li>
                <li>Uploaded images (including HEIC/HEIF from iOS), audio, and any reference media</li>
                <li>AI-generated images, videos, voiceovers, music, sound effects (SFX), and full cinematic films</li>
                <li>Movie-mode scripts and shot plans produced by Claude Sonnet 4.5</li>
                <li>FX Lab timeline projects, multi-clip compositions, and Director-Polish settings</li>
                <li>Community posts (only when you opt-in via &quot;Share to community&quot;), realm tag, likes, comments, follows, reports</li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">c. Payment data</p>
              <p>
                Card and crypto details are handled directly by PayPal, Stripe
                (where active), and supported crypto wallet providers; we
                receive only transaction identifiers and statuses —{" "}
                <strong>we never see your full card number or wallet private
                keys</strong>.
              </p>

              <p className="font-semibold text-ink-950 mt-4">d. Technical data</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address, user-agent, device type, OS, language, time-zone</li>
                <li>Pages visited, features used, A/B-test exposure events, click streams</li>
                <li>Login timestamps, failed-login counts (for brute-force protection)</li>
                <li>PWA / native-app install state, push-token (only if you opt-in to native push)</li>
                <li>Session-storage and local-storage state for auth tokens and onboarding flags</li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">e. Personalization signals (XEA Brain)</p>
              <p>
                To make the product learn from each user, XEA logs the
                prompts you generate from, the styles you select, the moods
                you favor, the durations you pick, and the outputs you
                publish vs. discard. These signals power per-user
                recommendations (e.g. default style, default duration, music
                vibe) and aggregate, anonymized signals across the user base.
                Aggregated signals are <strong>never</strong> tied back to
                an identifiable individual.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and operate the Service — Marketing, Designing, CGI / FX Lab, and Movie Mode</li>
                <li>Process payments and credit/deduct tokens, including automatic refunds on failed generations</li>
                <li>Send transactional emails (receipts, tip notifications, password resets, security alerts, weekly leaderboard digests)</li>
                <li>Send the optional <strong>Daily Email Reminder</strong> at a time you select in Settings (if enabled)</li>
                <li>Rank the Weekend Top 20 (per realm and overall) using automated, objective likes + comments counts</li>
                <li>Moderate user-generated content with automated safety filters across every prompt, caption, overlay, and uploaded media</li>
                <li>Improve the product through aggregated analytics, A/B testing, and XEA Brain personalization</li>
                <li>Gift tokens or subscription upgrades from the Admin Dashboard (logged as ledger entries)</li>
                <li>Comply with legal obligations and prevent fraud, abuse, multi-accounting, and CSAM (see §6 + §8.1 of Terms)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">4. Third-Party Service Providers</h2>
              <p>We share strictly the minimum data needed for these vendors to perform a function. Each vendor is bound by a Data Processing Agreement or equivalent contractual safeguard:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Anthropic (Claude Sonnet 4.5)</strong> — Movie Mode
                  planner, FX Lab &quot;Director Polish&quot;, and structured
                  reasoning. Receives your prompt and reference media URLs.
                </li>
                <li>
                  <strong>OpenAI</strong> — GPT Image 1 image generation,
                  Whisper transcription (where used), and selected language
                  features.
                </li>
                <li>
                  <strong>Google Gemini</strong> — Vibe Translator, vision
                  analysis, and Nano-Banana image generation.
                </li>
                <li>
                  <strong>DeepSeek</strong> — fast, low-cost Marketing-Mode
                  chat suggestions and prompt drafting. Receives the
                  Marketing-chat conversation only.
                </li>
                <li>
                  <strong>Kling AI</strong> — image-to-video and
                  text-to-video generation (incl. all Movie-Mode shots).
                  Receives your prompts and reference frames.
                </li>
                <li>
                  <strong>ElevenLabs</strong> — text-to-speech for Movie
                  narration / FX Lab voiceovers, AI vocal music, and SFX.
                  Receives the line / prompt to synthesize.
                </li>
                <li>
                  <strong>Mubert</strong> — AI-generated instrumental music
                  for video polish.
                </li>
                <li>
                  <strong>Jamendo</strong> — licensed royalty-free music
                  search.
                </li>
                <li>
                  <strong>Resend</strong> — delivers our transactional and
                  reminder emails.
                </li>
                <li>
                  <strong>PayPal</strong>, <strong>Stripe</strong> (where
                  active), and supported crypto processors — charge
                  subscriptions and token packs.
                </li>
                <li>
                  <strong>Cloudflare R2 / equivalent object storage</strong> —
                  hosts your media (images, videos, audio, voiceovers,
                  movies).
                </li>
                <li>
                  <strong>MongoDB Atlas</strong> — managed database hosting.
                </li>
                <li>
                  <strong>Emergent</strong> — application hosting,
                  infrastructure, and LLM key brokerage for the integrations
                  above where we use the Emergent Universal Key.
                </li>
                <li>
                  <strong>Capacitor / Ionic</strong> — native-app shell
                  packaging (no data leaves the device for Capacitor
                  itself).
                </li>
                <li>
                  <strong>fal.ai</strong> — currently gated / disabled. Will
                  be re-enabled in a future release; if so, we will publish a
                  policy update before any data is sent.
                </li>
              </ul>
              <p className="mt-2">
                We do <strong>not</strong> sell your personal data, and we do
                not use it for cross-context behavioural advertising.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">5. Community Visibility &amp; Realms</h2>
              <p>
                Community posts are visible to all logged-in XEA users
                inside the realm you publish to (Marketing, Designing, CGI,
                or Movie). When you publish to the community, the following
                becomes visible: your display name, your profile initial /
                avatar, the media you chose to share, the caption, the
                original prompt, the realm, and aggregated counts of likes
                and comments. Your email address, plan, exact token
                balance, ledger entries, and earnings balance are{" "}
                <strong>never</strong> visible to other users.
              </p>
              <p className="mt-2">
                A privacy-safe form of your name (e.g. &quot;Maria K.&quot;)
                may also appear in the public &quot;Top Earners&quot; ticker
                on the landing page if your earnings rank you in the top
                positions for the period. To opt out of public visibility
                entirely, email{" "}
                <a href="mailto:privacy@xe-a.com" className="text-coral-500 underline">
                  privacy@xe-a.com
                </a>{" "}
                and we will set the <code>exclude_from_public</code> flag on
                your account; you will then no longer appear in tickers,
                leaderboards, or community feeds.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">6. AI Moderation &amp; Zero-Tolerance Enforcement</h2>
              <p>
                We use automated content-safety models to screen prompts,
                captions, comments, edit prompts, negative prompts, text
                overlays, voiceover scripts, movie scripts, uploaded media,
                and shared posts for explicit, hateful, violent, or otherwise
                prohibited content. These models process your input in
                transit and are <strong>not</strong> used to train any
                public model of ours.
              </p>
              <p className="mt-2">
                Flagged generation attempts (including the prompt text, the
                endpoint, a timestamp, and account identifiers) are logged
                in our internal moderation queue for audit purposes. This
                log is retained for 24 months and is accessible only to our
                moderation and compliance staff.
              </p>
              <p className="mt-2">
                <strong>Any attempt to generate sexual, nude, or
                child-unsafe content — through any studio, any tool, or any
                field — results in immediate, permanent account
                termination across every studio. No warning, no appeal.
                See Terms §8.1.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">7. Data Retention</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Account &amp; profile: while your account is active.</li>
                <li>Generation outputs (images, videos, movies, voiceovers, music): while your account is active; you can delete them anytime from your dashboard / gallery.</li>
                <li>Community posts: until you unshare them or your account is closed.</li>
                <li>Token-activity ledger entries: 7 years (financial-audit law).</li>
                <li>Payment, tip, and earnings records: at least 7 years (tax/accounting law).</li>
                <li>Email log &amp; security log: 24 months.</li>
                <li>Moderation log (incl. flagged prompts): 24 months; CSAM-related identifiers may be retained longer where law requires.</li>
                <li>Anonymous analytics &amp; aggregated XEA-Brain signals: indefinitely.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">8. Your Rights (GDPR / CCPA / equivalent)</h2>
              <p>
                Subject to applicable law, you have the right to access,
                correct, export, restrict, or delete your personal data, and
                to object to certain processing (incl. opting out of the
                XEA-Brain personalization signals). You can exercise most of
                these directly from the Settings page; for the rest, write
                to{" "}
                <a href="mailto:privacy@xe-a.com" className="text-coral-500 underline">
                  privacy@xe-a.com
                </a>{" "}
                and we will respond within 30 days. California residents may
                also exercise their CCPA rights via the same address. You
                have the right to complain to your local data-protection
                authority.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">9. Security</h2>
              <p>
                We use TLS in transit, bcrypt-hashed passwords, brute-force
                lockouts, signed PayPal / Stripe webhook verification, strict
                CORS, role-based access control on admin endpoints, and
                rate-limited APIs. Tokens are scoped per-account and stored
                in <code>localStorage</code>; we recommend logging out of
                shared devices. No system is 100% secure — please use a
                strong unique password, never share credentials, and notify
                us immediately if you suspect unauthorized access.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">10. Communications</h2>
              <p>
                <strong>Transactional emails</strong> (receipts, tip
                notifications, password resets, security alerts, refund
                notices, ban / suspension notices) are required to operate
                the Service and cannot be opted out of while you have an
                active account.
              </p>
              <p className="mt-2">
                The optional <strong>Daily Email Reminder</strong> is sent
                only if you enable it in Settings. Browser push
                notifications have been retired; we no longer collect or
                store browser push subscription endpoints. Native-app push
                (Capacitor) is opt-in at install time and can be revoked
                from your OS settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">11. Children</h2>
              <p>
                XEA is not intended for users under 18 and is not directed
                at minors. We do not knowingly collect personal data from
                minors. If you believe a minor has provided us data, please
                contact us and we will delete it promptly. Any prompt
                attempting to generate content involving a minor in a
                sexual or unsafe context will trigger immediate permanent
                termination and may be reported to law enforcement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">12. International Transfers</h2>
              <p>
                Your data may be processed in the EU, US, UK, or other
                countries where our vendors operate. Where required, we rely
                on Standard Contractual Clauses, UK Addendum, or equivalent
                safeguards.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">13. Native App (PWA &amp; Capacitor)</h2>
              <p>
                XEA is available as a Progressive Web App (PWA) and as
                native iOS / Android shells packaged with Capacitor. The
                native shells use the same backend and apply the same
                policies as the web app. Operating-system level data (push
                tokens, install ID, device-info APIs) are governed
                additionally by the App Store / Google Play privacy labels
                published alongside the app.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">14. Changes</h2>
              <p>
                We will notify you of material changes via email and/or an
                in-app banner before they take effect. The current version
                is always available at this URL with the &quot;Last
                updated&quot; date at the top.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">15. Contact</h2>
              <p>
                Privacy questions:{" "}
                <a href="mailto:privacy@xe-a.com" className="text-coral-500 underline">
                  privacy@xe-a.com
                </a>
                . General support:{" "}
                <a href="mailto:support@xe-a.com" className="text-coral-500 underline">
                  support@xe-a.com
                </a>
                . Legal:{" "}
                <a href="mailto:legal@xe-a.com" className="text-coral-500 underline">
                  legal@xe-a.com
                </a>
                .
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
